import { Delete as DeleteIcon } from '@mui/icons-material';
import Button from '@mui/material/Button';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import MuiDialogTitle from '@mui/material/DialogTitle';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactElement } from 'react';
import { useContext } from 'react';
import { useSearchParams } from 'react-router';

import { ApplicationContext } from '~/shared/context/context';
import { useTermsQuery } from '~/shared/query-hooks/terms';
import { useDeleteProfile } from '~/shared/query-hooks/use-delete-profile';

import { useSnackbars } from '../../error-handling/SnackBars';
import { useMyProfilePermissionsQuery } from '../../query-hooks/profile';
import { route } from './constants';

export const ConfirmAccountDeletionDialog: FunctionComponent = () => {
    const { actions, translate } = useContext(ApplicationContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const deletingAccount = searchParams.get(route.searchParams.deletingAccount) === 'true';
    const { data: myProfilePermissions } = useMyProfilePermissionsQuery();
    const { mutate } = useDeleteProfile(() => {
        actions.logout();
    });
    const { showErrorSnackbar } = useSnackbars();

    if (!deletingAccount || is.falsy(myProfilePermissions?.canUserDeleteOwnProfile)) {
        return null;
    }

    const dismissDialog = (): void => {
        const search = new URLSearchParams(searchParams);
        search.delete(route.searchParams.deletingAccount);
        setSearchParams(search, { replace: true });
    };

    const deleteAccount = (): void =>
        mutate(undefined, {
            onError: showErrorSnackbar,
        });

    return (
        <MuiDialog aria-modal={true} open={deletingAccount} onClose={dismissDialog}>
            <MuiDialogTitle>{`${translate('dialogs.user-settings.delete-account')}?`}</MuiDialogTitle>
            <DialogContent>
                <DialogContentText>{translate('dialogs.user-settings.account-deletion-message')}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button
                    aria-label={translate('common.delete')}
                    color="error"
                    startIcon={<DeleteIcon />}
                    sx={{ mr: 1 }}
                    variant="outlined"
                    onClick={deleteAccount}
                >
                    {translate('common.delete')}
                </Button>
                <Button variant="outlined" onClick={dismissDialog}>
                    {translate('dialogs.user-settings.no-i-want-to-stay')}
                </Button>
            </DialogActions>
        </MuiDialog>
    );
};

export const PrivacyTermsDialog: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const { termsQuery } = useTermsQuery();
    const [searchParams, setSearchParams] = useSearchParams();
    const openGDPRDialog = searchParams.get(route.searchParams.openPrivacyStatement) === 'true';

    const dismissDialog = (): void => {
        const search = new URLSearchParams(searchParams);
        search.delete(route.searchParams.openPrivacyStatement);
        setSearchParams(search, { replace: true });
    };

    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
    const getDialogContent = (): ReactElement<any> => {
        if (termsQuery.isError) {
            return <DialogContent>{translate('pages.settings.data-privacy.dialog.load-error')}</DialogContent>;
        }

        if (termsQuery.isPending) {
            return <DialogContent>{translate('pages.settings.data-privacy.dialog.loading')}</DialogContent>;
        }

        const termsText = is.nonEmptyStringAndNotWhitespace(termsQuery.data.text)
            ? termsQuery.data.text
            : termsQuery.data.shortText;

        return (
            <DialogContent>
                {/** biome-ignore lint/security/noDangerouslySetInnerHtml: suppressed due to migration */}
                <DialogContentText dangerouslySetInnerHTML={{ __html: termsText }} />
            </DialogContent>
        );
    };

    return (
        <MuiDialog maxWidth="md" open={openGDPRDialog} onClose={dismissDialog}>
            <MuiDialogTitle>{translate('pages.settings.data-privacy.dialog.title')}</MuiDialogTitle>
            {getDialogContent()}
            <DialogActions>
                <Button onClick={dismissDialog}>{translate('common.close')}</Button>
            </DialogActions>
        </MuiDialog>
    );
};
